import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../../firebase";
import { Carousel } from 'react-responsive-carousel';
import "react-responsive-carousel/lib/styles/carousel.min.css";

export default function ProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("details");
  const [isMobile, setIsMobile] = useState(false);
  const [company, setCompany] = useState(null);
  const [similarProjects, setSimilarProjects] = useState([]);
  const [mapUrl, setMapUrl] = useState(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    async function fetchProject() {
      try {
        setLoading(true);
        const projectRef = doc(db, "projects", id);
        const projectSnap = await getDoc(projectRef);

        if (!projectSnap.exists()) {
          setError("Proje bulunamadı");
          setLoading(false);
          return;
        }

        const projectData = projectSnap.data();
        setProject({
          id: projectSnap.id,
          ...projectData
        });

        // Location details alt koleksiyonundan harita URL'sini al
        try {
          const locationDetailsRef = collection(projectRef, "location_details");
          const locationDocRef = doc(locationDetailsRef, "locations");
          const locationDocSnap = await getDoc(locationDocRef);
          
          if (locationDocSnap.exists()) {
            const locationData = locationDocSnap.data();
            if (locationData.google_maps_link) {
              const embedUrl = convertToEmbedUrl(locationData.google_maps_link);
              setMapUrl(embedUrl);
            }
          }
        } catch (err) {
          console.error("Konum bilgileri yüklenirken hata:", err);
        }

        // Şirket bilgilerini company_ref üzerinden al
        if (projectData.company_ref) {
          try {
            const companyDoc = await getDoc(projectData.company_ref);
            if (companyDoc.exists()) {
              setCompany(companyDoc.data());
            }
          } catch (err) {
            console.error("Şirket bilgisi yüklenirken hata:", err);
          }
        }

        // Benzer projeleri al
        fetchSimilarProjects(projectData);

      } catch (err) {
        console.error("Proje yüklenirken hata:", err);
        setError("Proje yüklenirken bir hata oluştu");
      } finally {
        setLoading(false);
      }
    }

    fetchProject();
  }, [id]);
    // Google Maps URL'ini embed formatına dönüştüren fonksiyon
  const convertToEmbedUrl = (url) => {
    if (!url) return null;
    
    // Eğer zaten embed URL ise direkt döndür
    if (url.includes('/embed?')) return url;
    
    // Eğer embed pb parametresi varsa (Google Maps embed formatı)
    if (url.includes('!1m18!')) return url;
    
    try {
      // Yönlendirme (directions) linkinden koordinatları çıkar
      // Örnek URL: /dir/38.4539975,27.1617682/38.3016968,26.3998909/
      const dirMatch = url.match(/\/dir\/([-\d.]+),([-\d.]+)\/([-\d.]+),([-\d.]+)/);
      
      if (dirMatch) {
        const lat1 = parseFloat(dirMatch[1]);
        const lng1 = parseFloat(dirMatch[2]);
        const lat2 = parseFloat(dirMatch[3]);
        const lng2 = parseFloat(dirMatch[4]);
        
        // Ortalama noktayı hesapla (veya sadece başlangıç noktasını kullan)
        const centerLat = (lat1 + lat2) / 2;
        const centerLng = (lng1 + lng2) / 2;
        
        // Zoom seviyesi (mesafeye göre ayarlanabilir)
        const zoom = 12;
        
        // Embed URL oluştur
        return `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d${zoom}0000!2d${centerLng}!3d${centerLat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2z${lat1},${lng1}!5e0!3m2!1str!2str!4v1`;
      }
      
      // Place ID veya diğer formatları kontrol et
      const placeMatch = url.match(/place\/([^/]+)/);
      if (placeMatch) {
        const placeName = encodeURIComponent(placeMatch[1]);
        return `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3000!2d0!3d0!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2z${placeName}!5e0!3m2!1str!2str!4v1`;
      }
      
      // Hiçbir pattern uymazsa orijinal URL'yi döndür (buton olarak açılacak)
      return url;
      
    } catch (error) {
      console.error("URL dönüştürme hatası:", error);
      return url;
    }
  };

  const fetchSimilarProjects = async (currentProject) => {
    try {
      const projectsRef = collection(db, "projects");
      let q;
      
      if (currentProject.city) {
        q = query(
          projectsRef, 
          where("city", "==", currentProject.city),
          where("__name__", "!=", id)
        );
      } else {
        q = query(projectsRef, where("__name__", "!=", id));
      }

      const snapshot = await getDocs(q);
      const projects = snapshot.docs.slice(0, 4).map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setSimilarProjects(projects);
    } catch (error) {
      console.error("Benzer projeler yüklenirken hata:", error);
    }
  };

  const formatPrice = (price) => {
    if (!price) return "Fiyat bilgisi yok";
    
    if (typeof price === 'string') {
      if (price.includes('TL') || price.includes('₺')) {
        return price;
      }
      const numericPrice = price.replace(/[^\d]/g, '');
      if (numericPrice) {
        return `${parseInt(numericPrice).toLocaleString('tr-TR')} TL`;
      }
    }
    
    if (typeof price === 'number') {
      return `${price.toLocaleString('tr-TR')} TL`;
    }
    
    return price;
  };

  const handleContact = () => {
    if (company?.phone) {
      window.open(`tel:${company.phone}`);
    } else if (company?.email) {
      window.open(`mailto:${company.email}`);
    }
  };

  const handleWhatsApp = () => {
    const message = `Merhaba, "${project?.project_name}" projesi hakkında bilgi almak istiyorum.`;
    const phone = company?.phone?.replace(/\s/g, '');
    if (phone) {
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p>Proje yükleniyor...</p>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div style={styles.errorContainer}>
        <h2>Hata</h2>
        <p>{error || "Proje bulunamadı"}</p>
        <button 
          style={styles.backButton}
          onClick={() => navigate("/all-projects")}
        >
          Projelere Dön
        </button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Üst Bilgi ve Navigasyon */}
      <div style={styles.header}>
        <button 
          style={styles.backButton}
          onClick={() => navigate(-1)}
        >
          <svg style={styles.backIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Geri
        </button>
        <h1 style={styles.projectTitle}>{project.project_name}</h1>
      </div>

      <div style={styles.mainContent}>
        {/* Görsel Galeri */}
        <div style={styles.gallerySection}>
          {project.image_urls && project.image_urls.length > 0 ? (
            <Carousel
              showArrows={true}
              showStatus={true}
              showThumbs={false}
              infiniteLoop={true}
              autoPlay={false}
              stopOnHover={true}
              swipeable={true}
              dynamicHeight={false}
              emulateTouch={true}
            >
              {project.image_urls.map((url, index) => (
                <div key={index}>
                  <img 
                    src={url} 
                    alt={`${project.project_name} - ${index + 1}`}
                    style={styles.carouselImage}
                  />
                </div>
              ))}
            </Carousel>
          ) : (
            <div style={styles.noImage}>
              <svg style={styles.noImageIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 10H8.01M12 10H12.01M16 10H16.01M6 16L12 10L18 16M6 16H18M6 16V18C6 18.5304 6.21071 19.0391 6.58579 19.4142C6.96086 19.7893 7.46957 20 8 20H16C16.5304 20 17.0391 19.7893 17.4142 19.4142C17.7893 19.0391 18 18.5304 18 18V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <p>Görsel bulunmuyor</p>
            </div>
          )}
        </div>

        {/* Hızlı Bilgiler Kartı */}
        <div style={styles.infoCard}>
          <div style={styles.priceSection}>
            <p style={styles.priceTitle}>Başlangıç Fiyatı</p>
            <p style={styles.price}>{formatPrice(project.price)}</p>
          </div>

          <div style={styles.quickDetails}>
            <div style={styles.quickDetailItem}>
              <svg style={styles.quickDetailIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2Z" stroke="currentColor" strokeWidth="2"/>
                <path d="M12 11.5C13.3807 11.5 14.5 10.3807 14.5 9C14.5 7.61929 13.3807 6.5 12 6.5C10.6193 6.5 9.5 7.61929 9.5 9C9.5 10.3807 10.6193 11.5 12 11.5Z" stroke="currentColor" strokeWidth="2"/>
              </svg>
              <span>{project.city || "Şehir belirtilmemiş"} / {project.district || "İlçe belirtilmemiş"}</span>
            </div>
            <div style={styles.quickDetailItem}>
              <svg style={styles.quickDetailIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 7V3M16 7V3M7 11H17M5 21H19C20.1046 21 21 20.1046 21 19V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7V19C3 20.1046 3.89543 21 5 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>{project.delivery_date || "Teslim tarihi belirtilmemiş"}</span>
            </div>
            <div style={styles.quickDetailItem}>
              <svg style={styles.quickDetailIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 21H5C3.89543 21 3 20.1046 3 19V5C3 3.89543 3.89543 3 5 3H19C20.1046 3 21 3.89543 21 5V19C21 20.1046 20.1046 21 19 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9 8H7V16H9V8Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M17 8H15V16H17V8Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>{project.project_type || "Proje tipi belirtilmemiş"}</span>
            </div>
          </div>
        </div>

        {/* Uygulama İndirme Banner */}
        <div style={styles.appBanner}>
          <div style={styles.appBannerContent}>
            <div style={styles.appBannerText}>
              <h3 style={styles.appBannerTitle}>Daha Detaylı Bilgi İçin</h3>
              <p style={styles.appBannerDescription}>
                Tüm proje detayları, özellikler ve eksiksiz bilgiler için uygulamamızı indirin
              </p>
            </div>

            <div style={styles.appButtons}>
              
              {/* App Store */}
              <a
                href="https://apps.apple.com/tr/app/temelden/id6758143279?l=tr"
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: "none" }}
              >
                <button style={styles.appStoreButton}>
                  <svg style={styles.appIcon} viewBox="0 0 24 24" fill="none">
                    <path d="M17.05 20.28C16.07 21.23 15 21.08 13.97 20.63C12.88 20.17 11.88 20.15 10.73 20.63C9.29 21.25 8.53 21.07 7.67 20.28C2.78 15.25 3.51 7.59 9.05 7.31C10.4 7.38 11.34 8.05 12.13 8.11C13.31 7.87 14.44 7.18 15.7 7.27C17.21 7.39 18.35 7.99 19.1 9.07C15.98 10.94 16.72 15.05 19.58 16.2C19.01 17.7 18.27 19.19 17.04 20.29L17.05 20.28ZM12.03 7.25C11.88 4.52 14.2 2.42 16.85 2C17.2 4.96 14.72 7.18 12.03 7.25Z" fill="currentColor"/>
                  </svg>
                  App Store
                </button>
              </a>

              {/* Google Play */}
              <a
                href="https://play.google.com/store/apps/details?id=com.vondijital.temelden_app&hl=tr"
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: "none" }}
              >
                <button style={styles.playStoreButton}>
                  <svg style={styles.appIcon} viewBox="0 0 24 24" fill="none">
                    <path d="M3 20.5V3.5C3 2.4 3.9 1.5 5 1.5H19C20.1 1.5 21 2.4 21 3.5V20.5C21 21.6 20.1 22.5 19 22.5H5C3.9 22.5 3 21.6 3 20.5Z" stroke="currentColor" strokeWidth="2"/>
                    <path d="M15 8.5H9V15.5H15V8.5Z" stroke="currentColor" strokeWidth="2"/>
                    <path d="M9 11.5H15" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  Google Play
                </button>
              </a>

            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div style={styles.tabContainer}>
          <button 
            style={{
              ...styles.tabButton,
              ...(activeTab === "details" && styles.activeTabButton)
            }}
            onClick={() => setActiveTab("details")}
          >
            Proje Detayları
          </button>
          <button 
            style={{
              ...styles.tabButton,
              ...(activeTab === "location" && styles.activeTabButton)
            }}
            onClick={() => setActiveTab("location")}
          >
            Konum
          </button>
          <button 
            style={{
              ...styles.tabButton,
              ...(activeTab === "company" && styles.activeTabButton)
            }}
            onClick={() => setActiveTab("company")}
          >
            Şirket Bilgileri
          </button>
        </div>

        {/* Tab Content */}
        <div style={styles.tabContent}>
          {activeTab === "details" && (
            <div style={styles.detailsSection}>
              <h3 style={styles.sectionTitle}>Proje Açıklaması</h3>
              <p style={styles.description}>
                {project.project_description || "Açıklama bulunmuyor."}
              </p>

              <div style={styles.detailsGrid}>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Şehir:</span>
                  <span style={styles.detailValue}>{project.city || "Belirtilmemiş"}</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>İlçe:</span>
                  <span style={styles.detailValue}>{project.district || "Belirtilmemiş"}</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Teslim Tarihi:</span>
                  <span style={styles.detailValue}>{project.delivery_date || "Belirtilmemiş"}</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Proje Tipi:</span>
                  <span style={styles.detailValue}>{project.project_type || "Belirtilmemiş"}</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Fiyat:</span>
                  <span style={styles.detailValue}>{formatPrice(project.price)}</span>
                </div>
              </div>
            </div>
          )}

          {activeTab === "location" && (
            <div style={styles.locationSection}>
              <h3 style={styles.sectionTitle}>Konum Bilgileri</h3>

              <div style={styles.locationDetails}>
                <div style={styles.locationItem}>
                  <svg
                    style={styles.locationIcon}
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z"
                      fill="currentColor"
                    />
                  </svg>

                  <div>
                    <p style={styles.locationText}>
                      {project.city || "Şehir belirtilmemiş"}
                    </p>
                    <p style={styles.districtText}>
                      {project.district || "İlçe belirtilmemiş"}
                    </p>
                  </div>
                </div>

                {/* Google Maps - location_details alt koleksiyonundan alınan URL */}
                <div style={styles.mapContainer}>
                  {mapUrl && mapUrl.includes('embed') ? (
                    <iframe
                      src={mapUrl}
                      width="100%"
                      height="300"
                      style={{ border: 0, borderRadius: "10px" }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Google Maps"
                    ></iframe>
                  ) : mapUrl ? (
                    <div style={styles.mapPlaceholder}>
                      <svg style={styles.mapPlaceholderIcon} viewBox="0 0 24 24" fill="none">
                        <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2Z" stroke="currentColor" strokeWidth="2"/>
                        <path d="M12 11.5C13.3807 11.5 14.5 10.3807 14.5 9C14.5 7.61929 13.3807 6.5 12 6.5C10.6193 6.5 9.5 7.61929 9.5 9C9.5 10.3807 10.6193 11.5 12 11.5Z" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                      <p>Haritayı görüntülemek için tıklayın</p>
                      <a 
                        href={mapUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={styles.mapLink}
                      >
                        Google Maps'te Aç →
                      </a>
                    </div>
                  ) : (
                    <div style={styles.mapPlaceholder}>
                      <svg style={styles.mapPlaceholderIcon} viewBox="0 0 24 24" fill="none">
                        <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2Z" stroke="currentColor" strokeWidth="2"/>
                        <path d="M12 11.5C13.3807 11.5 14.5 10.3807 14.5 9C14.5 7.61929 13.3807 6.5 12 6.5C10.6193 6.5 9.5 7.61929 9.5 9C9.5 10.3807 10.6193 11.5 12 11.5Z" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                      <p>Harita bilgisi bulunamadı</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === "company" && (
            <div style={styles.companySection}>
              <h3 style={styles.sectionTitle}>Şirket Bilgileri</h3>
              {company ? (
                <div style={styles.companyDetails}>
                  <div style={styles.companyItem}>
                    <span style={styles.companyLabel}>Şirket Adı:</span>
                    <span style={styles.companyValue}>{company.company_name || "Belirtilmemiş"}</span>
                  </div>
                  <div style={styles.companyItem}>
                    <span style={styles.companyLabel}>Telefon:</span>
                    <span style={styles.companyValue}>{company.phone || "Belirtilmemiş"}</span>
                  </div>
                  <div style={styles.companyItem}>
                    <span style={styles.companyLabel}>E-posta:</span>
                    <span style={styles.companyValue}>{company.email || "Belirtilmemiş"}</span>
                  </div>
                  <div style={styles.companyItem}>
                    <span style={styles.companyLabel}>Konum:</span>
                    <span style={styles.companyValue}>{company.location || "Belirtilmemiş"}</span>
                  </div>
                  <div style={styles.companyItem}>
                    <span style={styles.companyLabel}>Web Sitesi:</span>
                    <span style={styles.companyValue}>
                      {company.website ? (
                        <a href={company.website} target="_blank" rel="noopener noreferrer" style={styles.websiteLink}>
                          {company.website}
                        </a>
                      ) : "Belirtilmemiş"}
                    </span>
                  </div>
                  {company.description && (
                    <div style={styles.companyDescription}>
                      <span style={styles.companyLabel}>Hakkında:</span>
                      <p style={styles.companyValue}>{company.description}</p>
                    </div>
                  )}
                </div>
              ) : (
                <p style={styles.noCompany}>Şirket bilgileri bulunamadı.</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Benzer Projeler */}
{similarProjects.length > 0 && (
  <div style={styles.similarProjects}>
    <div style={styles.similarHeader}>
      <h3 style={styles.similarTitle}>Benzer Projeler</h3>
      <button 
        style={styles.viewAllButton}
        onClick={() => navigate("/projects/all")}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "#1a3a5c";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "#2c3e50";
          e.currentTarget.style.transform = "translateY(0)";
        }}
      >
        Tüm Projeleri Gör
        <svg style={styles.viewAllIcon} viewBox="0 0 24 24" fill="none">
          <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
    </div>
    <div style={styles.similarGrid}>
      {similarProjects.map(project => (
        <div 
          key={project.id} 
          style={styles.similarCard}
          onClick={() => navigate(`/project/${project.id}`)}
        >
          <img 
            src={project.image_urls?.[0] || "https://via.placeholder.com/200x120?text=No+Image"} 
            alt={project.project_name}
            style={styles.similarImage}
          />
          <div style={styles.similarContent}>
            <h4 style={styles.similarProjectName}>{project.project_name}</h4>
            <p style={styles.similarPrice}>{formatPrice(project.price)}</p>
            <p style={styles.similarLocation}>{project.city}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
)}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "20px",
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "20px",
    marginBottom: "30px",
    paddingBottom: "20px",
    borderBottom: "1px solid #e0e0e0",
  },
  backButton: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 16px",
    backgroundColor: "#f8f9fa",
    border: "1px solid #dee2e6",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    color: "#495057",
    textDecoration: "none",
  },
  backIcon: {
    width: "16px",
    height: "16px",
  },
  projectTitle: {
    fontSize: "28px",
    fontWeight: "600",
    color: "#2c3e50",
    margin: 0,
  },
  mainContent: {
    display: "flex",
    flexDirection: "column",
    gap: "30px",
  },
  gallerySection: {
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  },
  carouselImage: {
    width: "100%",
    height: "400px",
    objectFit: "cover",
  },
  noImage: {
    height: "300px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f8f9fa",
    color: "#6c757d",
  },
  noImageIcon: {
    width: "48px",
    height: "48px",
    marginBottom: "16px",
    color: "#adb5bd",
  },
  infoCard: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "24px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    border: "1px solid #e0e0e0",
  },
  priceSection: {
    textAlign: "center",
    marginBottom: "20px",
    paddingBottom: "20px",
    borderBottom: "1px solid #e0e0e0",
  },
  priceTitle: {
    fontSize: "16px",
    color: "#6c757d",
    margin: "0 0 8px 0",
  },
  price: {
    fontSize: "24px",
    fontWeight: "700",
    color: "#2a9d8f",
    margin: 0,
  },
  quickDetails: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    marginBottom: "24px",
  },
  quickDetailItem: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "14px",
    color: "#495057",
  },
  quickDetailIcon: {
    width: "16px",
    height: "16px",
    color: "#6c757d",
  },
  contactButtons: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  primaryButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    padding: "12px 16px",
    backgroundColor: "#2a9d8f",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "600",
    transition: "background-color 0.3s ease",
  },
  whatsappButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    padding: "12px 16px",
    backgroundColor: "#25D366",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "600",
    transition: "background-color 0.3s ease",
  },
  buttonIcon: {
    width: "20px",
    height: "20px",
  },
  appBanner: {
    backgroundColor: "#2c3e50",
    borderRadius: "12px",
    padding: "24px",
    color: "white",
  },
  appBannerContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "20px",
  },
  appBannerText: {
    flex: 1,
    minWidth: "250px",
  },
  appBannerTitle: {
    fontSize: "20px",
    fontWeight: "600",
    margin: "0 0 8px 0",
    color: "white",
  },
  appBannerDescription: {
    fontSize: "14px",
    margin: 0,
    opacity: 0.9,
  },
  appButtons: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
  },
  appStoreButton: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 16px",
    backgroundColor: "rgba(255,255,255,0.1)",
    color: "white",
    border: "1px solid rgba(255,255,255,0.3)",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    backdropFilter: "blur(10px)",
  },
  playStoreButton: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 16px",
    backgroundColor: "rgba(255,255,255,0.1)",
    color: "white",
    border: "1px solid rgba(255,255,255,0.3)",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    backdropFilter: "blur(10px)",
  },
  appIcon: {
    width: "18px",
    height: "18px",
  },
  tabContainer: {
    display: "flex",
    borderBottom: "1px solid #e0e0e0",
    gap: "0",
    overflowX: "auto",
  },
  tabButton: {
    flex: 1,
    padding: "12px 16px",
    backgroundColor: "transparent",
    border: "none",
    borderBottom: "2px solid transparent",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    color: "#6c757d",
    transition: "all 0.3s ease",
    minWidth: "120px",
  },
  activeTabButton: {
    color: "#2a9d8f",
    borderBottomColor: "#2a9d8f",
    backgroundColor: "#f8f9fa",
  },
  tabContent: {
    padding: "20px 0",
  },
  sectionTitle: {
    fontSize: "20px",
    fontWeight: "600",
    color: "#2c3e50",
    marginBottom: "16px",
  },
  description: {
    fontSize: "16px",
    lineHeight: "1.6",
    color: "#495057",
    marginBottom: "24px",
  },
  detailsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: "16px",
  },
  detailItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px",
    backgroundColor: "#f8f9fa",
    borderRadius: "6px",
  },
  detailLabel: {
    fontWeight: "600",
    color: "#495057",
  },
  detailValue: {
    color: "#6c757d",
  },
  locationSection: {},
  locationDetails: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  locationItem: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "16px",
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
  },
  locationIcon: {
    width: "24px",
    height: "24px",
    color: "#2a9d8f",
  },
  locationText: {
    fontWeight: "600",
    color: "#2c3e50",
    margin: 0,
  },
  districtText: {
    color: "#6c757d",
    margin: 0,
  },
  mapContainer: {
    width: "100%",
    borderRadius: "10px",
    overflow: "hidden",
  },
  mapLink: {
  display: "inline-block",
  marginTop: "12px",
  padding: "8px 16px",
  backgroundColor: "#2a9d8f",
  color: "#fff",
  textDecoration: "none",
  borderRadius: "6px",
  fontSize: "14px",
  fontWeight: "500",
  transition: "background-color 0.3s ease",
  '&:hover': {
    backgroundColor: "#21867a",
  }
},
  mapPlaceholder: {
    height: "300px",
    backgroundColor: "#e9ecef",
    borderRadius: "10px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    color: "#6c757d",
    gap: "12px",
  },
  mapPlaceholderIcon: {
    width: "48px",
    height: "48px",
    color: "#adb5bd",
  },
  companySection: {},
  companyDetails: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  companyItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px",
    backgroundColor: "#f8f9fa",
    borderRadius: "6px",
  },
  companyLabel: {
    fontWeight: "600",
    color: "#495057",
  },
  companyValue: {
    color: "#6c757d",
  },
  companyDescription: {
    padding: "12px",
    backgroundColor: "#f8f9fa",
    borderRadius: "6px",
  },
  websiteLink: {
    color: "#2a9d8f",
    textDecoration: "none",
  },
  noCompany: {
    textAlign: "center",
    color: "#6c757d",
    fontStyle: "italic",
  },
  similarHeader: {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "20px",
  flexWrap: "wrap",
  gap: "15px",
},
viewAllButton: {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "10px 20px",
  backgroundColor: "#001E78",
  color: "white",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
  fontSize: "14px",
  fontWeight: "600",
  fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  transition: "all 0.3s ease",
  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
},
viewAllIcon: {
  width: "16px",
  height: "16px",
  stroke: "white",
  strokeWidth: "2",
},
  similarProjects: {
    marginTop: "40px",
    paddingTop: "40px",
    borderTop: "1px solid #e0e0e0",
  },
  similarTitle: {
    fontSize: "24px",
    fontWeight: "600",
    color: "#2c3e50",
    marginBottom: "20px",
  },
  similarGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: "20px",
  },
  similarCard: {
    backgroundColor: "#fff",
    borderRadius: "8px",
    overflow: "hidden",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    cursor: "pointer",
    transition: "transform 0.3s ease, box-shadow 0.3s ease",
  },
  similarImage: {
    width: "100%",
    height: "120px",
    objectFit: "cover",
  },
  similarContent: {
    padding: "12px",
  },
  similarProjectName: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#2c3e50",
    margin: "0 0 8px 0",
  },
  similarPrice: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#2a9d8f",
    margin: "0 0 4px 0",
  },
  similarLocation: {
    fontSize: "12px",
    color: "#6c757d",
    margin: 0,
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "50vh",
    gap: "16px",
  },
  spinner: {
    width: "40px",
    height: "40px",
    border: "4px solid #f3f3f3",
    borderTop: "4px solid #2a9d8f",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  errorContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "50vh",
    gap: "16px",
    textAlign: "center",
  },
};

// CSS animations
if (typeof document !== 'undefined') {
  const styleSheet = document.styleSheets[0];
  const spinStyle = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;
  
  const mobileStyles = `
    @media (max-width: 768px) {
      .container {
        padding: 15px;
      }
      .projectTitle {
        font-size: 22px;
      }
      .carouselImage {
        height: 250px;
      }
      .detailsGrid {
        grid-template-columns: 1fr;
      }
      .similarGrid {
        grid-template-columns: 1fr;
      }
      .appBannerContent {
        flex-direction: column;
        text-align: center;
      }
      .appButtons {
        justify-content: center;
      }
      .tabContainer {
        flex-wrap: nowrap;
        overflow-x: auto;
      }
      .tabButton {
        min-width: 140px;
      }
      .companyItem {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
      }
    }

    @media (max-width: 480px) {
      .container {
        padding: 10px;
      }
      .header {
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;
      }
      .projectTitle {
        font-size: 20px;
      }
      .carouselImage {
        height: 200px;
      }
      .infoCard {
        padding: 16px;
      }
      .appBanner {
        padding: 16px;
      }
      .appButtons {
        flex-direction: column;
        width: 100%;
      }
      .appStoreButton,
      .playStoreButton {
        width: 100%;
        justify-content: center;
      }
    }
  `;

  const style = document.createElement('style');
  style.textContent = spinStyle + mobileStyles;
  document.head.appendChild(style);
}